import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { useQueryClient } from '@tanstack/react-query';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { createInsightQueryOptions } from '@terminal/react-query-hooks/insights/useInsight';
import { createKgInsightQueryOptions } from '@terminal/react-query-hooks/insights/useKgInsight';
import { Tracking } from '@terminal/services/tracking';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useCatalystDrawer } from '@terminal/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { useEarningsInsightDrawer } from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import { MouseEvent } from 'react';

import { PulseSentimentDirection } from '../pulse-types';
import { getDailyCitationInsightId } from '../utils/linkDailySummaryCitations';
import { openDrawerForInsight } from '../utils/openDrawerForInsight';
import { toPulseInsight } from '../utils/toPulseInsight';

const EMPTY_SCENARIO_INSIGHT_IDS = new Set<string>();

const isScenarioInsight = (
  insight: KgInsight | DetailedInsight
): insight is DetailedInsight => 'metadata' in insight;

export const usePulseCitationClick = <
  TInsight extends KgInsight | DetailedInsight,
>({
  themeId,
  insightsById,
  scenarioInsightIds = EMPTY_SCENARIO_INSIGHT_IDS,
  direction = 'tailwind',
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
}: {
  themeId?: string;
  insightsById: Map<string, TInsight>;
  scenarioInsightIds?: ReadonlySet<string>;
  direction?: PulseSentimentDirection;
  source?: PosthogTrackingSource;
}) => {
  const queryClient = useQueryClient();
  const { openDrawer: openScenarioInsightDrawer } = useInsightDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { openDrawer: openCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openEarningsInsightDrawer } = useEarningsInsightDrawer();

  const openKgInsight = (insight: KgInsight) => {
    openDrawerForInsight({
      insight: toPulseInsight(insight, direction),
      source,
      fallbackThemeId: themeId,
      openMarketCatalystDrawer,
      openCatalystDrawer,
      openEarningsInsightDrawer,
    });
  };

  const openScenarioInsight = (insight: DetailedInsight) => {
    openScenarioInsightDrawer({
      id: insight.id,
      entityTag: insight.metadata.entity,
      trackingProperties: { source },
    });
  };

  const openCitation = async (insightId: string) => {
    // Citations render before the prefetch resolves, so a click can land while
    // an insight is still in flight (or after it failed) — fetch it rather than
    // dropping the click.
    let insight: KgInsight | DetailedInsight | undefined =
      insightsById.get(insightId);

    if (!insight) {
      try {
        if (scenarioInsightIds.has(insightId)) {
          insight = await queryClient.query({
            ...createInsightQueryOptions(insightId),
            staleTime: 'static',
          });
        } else {
          insight = await queryClient.query({
            ...createKgInsightQueryOptions(insightId),
            staleTime: 'static',
          });
        }
      } catch (error) {
        Tracking.captureException(error);
        return;
      }
    }

    if (isScenarioInsight(insight)) {
      openScenarioInsight(insight);
      return;
    }

    openKgInsight(insight);
  };

  const handleCitationClick = (event: MouseEvent): boolean => {
    if (!(event.target instanceof Element)) {
      return false;
    }

    const href = event.target.closest('a')?.getAttribute('href');
    const insightId = getDailyCitationInsightId(href ?? null);
    if (!insightId) {
      return false;
    }

    event.preventDefault();
    void openCitation(insightId);
    return true;
  };

  return { handleCitationClick, openKgInsight };
};
